import React from 'react';
import { Plus, Trash2, CheckSquare, Square, Calendar, Link2, Wand2 } from 'lucide-react';

const PRIORITY_CFG = {
  low:    { label: 'Low',    color: 'text-slate-500  bg-slate-50 border-slate-200'  },
  medium: { label: 'Medium', color: 'text-amber-700  bg-amber-50 border-amber-200'  },
  high:   { label: 'High',   color: 'text-red-700    bg-red-50   border-red-200'    },
};

const fmtDueDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(d); target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return d.toLocaleDateString(undefined, { weekday: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const isOverdue = (iso) => {
  if (!iso) return false;
  const due = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return due < today;
};

// Flatten a hierarchical task tree into a single ordered list of
// { task, depth } pairs so we can render with a simple .map() and
// avoid any self-referential JSX (which trips babel-loader's traversal).
function flattenTree(parents, childrenByParent) {
  const out = [];
  const walk = (task, depth) => {
    out.push({ task, depth });
    const kids = childrenByParent[task.task_id] || [];
    kids.forEach(k => walk(k, depth + 1));
  };
  parents.forEach(p => walk(p, 0));
  return out;
}

export default function TreeView({
  parents, childrenByParent, estimateById,
  onEdit, onDelete, onToggleDone, onAddSubtask, onBreakdown,
}) {
  if (parents.length === 0) {
    return (
      <div className="bg-white border border-[#E6EBE8] rounded-md p-8 text-center">
        <p className="text-sm text-[#6B7A73]">No tasks to show yet. Add one to start building your tree.</p>
      </div>
    );
  }

  const flat = flattenTree(parents, childrenByParent);

  return (
    <div className="bg-white border border-[#E6EBE8] rounded-md p-4 space-y-1" data-testid="tree-view">
      {flat.map(({ task, depth }) => {
        const kids = childrenByParent[task.task_id] || [];
        const done = task.status === 'done';
        const est = task.estimate_id ? estimateById.get(task.estimate_id) : null;
        const prioCfg = PRIORITY_CFG[task.priority];
        return (
          <div
            key={task.task_id}
            className={`group flex items-start gap-2 px-2 py-1.5 rounded hover:bg-[#F9FAFA] ${
              depth > 0 ? 'border-l-2 border-[#E6EBE8]' : ''
            }`}
            style={{ marginLeft: depth * 20 }}
            data-testid={`tree-node-${task.task_id}`}
          >
            <button onClick={() => onToggleDone(task)} className="mt-0.5">
              {done
                ? <CheckSquare className="w-4 h-4 text-[#2D6B68]" />
                : <Square className="w-4 h-4 text-[#6B7A73] hover:text-[#2D6B68]" />}
            </button>
            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onEdit(task)}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm ${done ? 'line-through text-[#6B7A73]' : 'text-[#1A1D1C]'}`}>
                  {task.title}
                </span>
                {task.priority !== 'medium' && prioCfg && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${prioCfg.color}`}>
                    {prioCfg.label}
                  </span>
                )}
                {est && (
                  <span className="text-[10px] text-[#C7892A] inline-flex items-center gap-0.5">
                    <Link2 className="w-2.5 h-2.5" /> {est.estimate_number}
                  </span>
                )}
                {task.due_date && (
                  <span className={`text-[10px] ${isOverdue(task.due_date) && !done ? 'text-red-700' : 'text-[#6B7A73]'}`}>
                    <Calendar className="w-2.5 h-2.5 inline mr-0.5" />{fmtDueDate(task.due_date)}
                  </span>
                )}
                {kids.length > 0 && (
                  <span
                    className="text-[10px] text-[#6B7A73]"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {kids.filter(k => k.status === 'done').length}/{kids.length}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {onBreakdown && (
                <button
                  onClick={() => onBreakdown(task)}
                  className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-white rounded"
                  title="AI break down"
                >
                  <Wand2 className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => onAddSubtask(task.task_id)}
                className="p-1 text-[#6B7A73] hover:text-[#2D6B68] hover:bg-white rounded"
                title="Add child"
              >
                <Plus className="w-3 h-3" />
              </button>
              <button
                onClick={() => onDelete(task.task_id)}
                className="p-1 text-[#6B7A73] hover:text-red-600 hover:bg-white rounded"
                title="Delete"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
